/**
 * 未找到路由外部承接守卫。
 *
 * 职责：
 * 1. 仅处理 `not-found` 路由，避免影响编辑器、预览页等业务入口。
 * 2. 根据当前会话状态把未知地址交给外部用户中心或外部门户承接。
 * 3. 使用显式外部地址跳转，避免在前端无首页路由时再次落入 catch-all 循环。
 */

import type { NavigationGuardNext, NavigationGuardWithThis, RouteLocationNormalized } from 'vue-router';
import { useAuthStore } from '@/stores/auth';
import { apiConfig } from '@/services/api/config';

/** 未找到路由的名称，供守卫与路由表保持同一语义。 */
export const NOT_FOUND_ROUTE_NAME = 'not-found';

/**
 * 未找到路由外部承接守卫依赖。
 *
 * 使用依赖注入让跳转目标和浏览器跳转动作可测试，避免单元测试直接改写 `window.location`。
 */
export interface NotFoundExternalRedirectGuardDeps {
  /** 读取当前用户是否已经完成认证。 */
  getIsAuthenticated: () => boolean;

  /** 读取已登录用户的外部个人中心地址。 */
  getExternalProfileUrl: () => string;

  /** 读取未登录用户的外部门户首页地址。 */
  getExternalHomeUrl: () => string;

  /** 执行浏览器外部跳转。 */
  assignLocation: (url: string) => void;
}

/**
 * 创建未找到路由外部承接守卫。
 *
 * @param deps 守卫依赖集合。
 * @returns Vue Router 导航守卫函数。
 */
export function createNotFoundExternalRedirectGuard(
  deps: NotFoundExternalRedirectGuardDeps,
) {
  return (
    to: RouteLocationNormalized,
    _from: RouteLocationNormalized,
    next: NavigationGuardNext,
  ): void => {
    if (to.name !== NOT_FOUND_ROUTE_NAME) {
      next();
      return;
    }

    const targetUrl = deps.getIsAuthenticated()
      ? deps.getExternalProfileUrl()
      : deps.getExternalHomeUrl();

    /**
     * 这里先发起外部跳转，再中断当前 SPA 导航。
     * 原因是未知路径已经没有内部页面语义，继续解析只会让 catch-all 再次参与匹配。
     */
    deps.assignLocation(targetUrl);
    next(false);
  };
}

/**
 * 默认未找到路由外部承接守卫。
 *
 * 该守卫依赖 `initSessionGuard` 先执行，以便直接使用初始化后的认证状态。
 */
export const notFoundExternalRedirectGuard: NavigationGuardWithThis<undefined> = (to, from, next) => {
  const authStore = useAuthStore();
  const guard = createNotFoundExternalRedirectGuard({
    getIsAuthenticated: () => authStore.isAuthenticated,
    getExternalProfileUrl: () => apiConfig.externalProfileUrl,
    getExternalHomeUrl: () => apiConfig.externalHomeUrl,
    assignLocation: (url) => {
      window.location.href = url;
    },
  });

  return guard(to, from, next);
};
